import AsyncStorage from '@react-native-async-storage/async-storage';

import { User } from '../models/User';
import { UserAttributeSource } from '../models/UserAttribute';

export interface WearableConnectionState {
  userId: number;
  fitbitAccessToken?: string | null;
  garminConfig?: User['garminConfig'] | null;
  ouraAccessToken?: string | null;
}

export function displayedWearableConnection(
  user?: User | null
): WearableConnectionState | undefined {
  if (!user?.id) return undefined;
  if (user.wearableConnection?.userId) return user.wearableConnection;
  return {
    userId: user.id,
    fitbitAccessToken: user.fitbitAccessToken,
    garminConfig: user.garminConfig,
    ouraAccessToken: user.ouraAccessToken
  };
}

export function withoutWearableCredential(
  user: User,
  field: 'fitbitAccessToken' | 'ouraAccessToken' | 'garminConfig'
): User {
  const connection = displayedWearableConnection(user);
  if (!connection) return user;
  const wearableConnection = { ...connection, [field]: undefined };
  if (connection.userId === user.id) {
    return { ...user, [field]: undefined, wearableConnection };
  }
  return { ...user, wearableConnection };
}

const phoneHealthOwnerKey = (source: UserAttributeSource) =>
  `phone-health-owner:${source}`;

const phoneHealthSyncFromKey = (source: UserAttributeSource) =>
  `phone-health-sync-from:${source}`;

export function isPhoneHealthSource(source: UserAttributeSource) {
  return (
    source === UserAttributeSource.HealthKit ||
    source === UserAttributeSource.GoogleFit ||
    source === UserAttributeSource.HealthConnect
  );
}

export async function bindPhoneHealth(
  source: UserAttributeSource,
  userId: number,
  sessionUserId: number
) {
  const current = await getPhoneHealthOwner(source);
  if (
    (current != null && current !== userId) ||
    (current == null && userId !== sessionUserId)
  ) {
    await AsyncStorage.setItem(
      phoneHealthSyncFromKey(source),
      new Date().toISOString()
    );
  } else if (current == null) {
    await AsyncStorage.removeItem(phoneHealthSyncFromKey(source));
  }
  await AsyncStorage.setItem(phoneHealthOwnerKey(source), String(userId));
}

export async function getPhoneHealthSyncFrom(
  source: UserAttributeSource
): Promise<Date | null> {
  const raw = await AsyncStorage.getItem(phoneHealthSyncFromKey(source));
  if (!raw) return null;
  const syncFrom = new Date(raw);
  return Number.isNaN(syncFrom.getTime()) ? null : syncFrom;
}

export function clampPhoneHealthStart(start: Date, syncFrom: Date | null) {
  if (!syncFrom || syncFrom <= start) return start;
  return syncFrom;
}

export function getPhoneHealthQueryStart(
  start: Date,
  syncFrom: Date | null
) {
  if (syncFrom && syncFrom >= start) return start;
  return new Date(start.getTime() + 1000);
}

export async function getPhoneHealthOwner(
  source: UserAttributeSource
): Promise<number | null> {
  const raw = await AsyncStorage.getItem(phoneHealthOwnerKey(source));
  if (!raw) return null;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * A phone-health permission with no stored owner belongs to the signed-in
 * user only while the connection itself belongs to that user.
 */
export function phoneHealthCountsFor(
  ownerId: number | null,
  connectionUserId: number,
  sessionUserId: number
) {
  if (ownerId == null) return connectionUserId === sessionUserId;
  return ownerId === connectionUserId;
}

export async function phoneHealthIsConnected(
  source: UserAttributeSource,
  osAuthorized: boolean,
  user?: User | null
) {
  if (!osAuthorized || !user?.id) return false;
  const connection = displayedWearableConnection(user);
  if (!connection) return false;
  const ownerId = await getPhoneHealthOwner(source);
  return phoneHealthCountsFor(ownerId, connection.userId, user.id);
}

export async function bindDisplayedPhoneHealth(
  source: UserAttributeSource,
  user?: User | null
) {
  const connection = displayedWearableConnection(user);
  if (!connection) return;
  if (!user?.id) return;
  await bindPhoneHealth(source, connection.userId, user.id);
}
